import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';

// High quality destination images
const IMAGES = {
  korea: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80",
  japan: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80",
  china: "https://images.unsplash.com/photo-1474181487882-5abf3f0ba6c2?w=800&q=80",
  spain: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"
};

const ExperiencesSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const experiences = [
    { key: 'korea', image: IMAGES.korea, ...t.experiences.korea },
    { key: 'japan', image: IMAGES.japan, ...t.experiences.japan },
    { key: 'china', image: IMAGES.china, ...t.experiences.china },
    { key: 'spain', image: IMAGES.spain, ...t.experiences.spain },
  ];

  return (
    <section 
      id="experiences" 
      ref={sectionRef}
      className="py-32 md:py-40 bg-soft-black relative"
      data-testid="experiences-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-20 md:mb-24 animate-on-scroll">
          <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-zinc-500 mb-4">
            {t.experiences.overline}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-white mb-6">
            {t.experiences.title}
          </h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto font-light">
            {t.experiences.subtitle}
          </p>
          <div className="w-16 h-px bg-white/20 mx-auto mt-8" />
        </div>

        {/* Experiences Grid - 2x2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {experiences.map((exp, index) => (
            <div 
              key={exp.key}
              className="animate-on-scroll"
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div 
                className="group relative h-[400px] md:h-[450px] overflow-hidden border border-white/5 hover:border-white/20 transition-all duration-700"
                data-testid={`experience-${exp.key}`}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img 
                    src={exp.image} 
                    alt={exp.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 filter grayscale group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
                </div>

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10">
                  {/* Location badge */}
                  <span className="inline-block px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-white/60 border border-white/20 w-fit mb-4">
                    {exp.location}
                  </span>
                  
                  <h3 className="font-serif text-2xl md:text-3xl font-normal text-white mb-3">
                    {exp.title}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                    {exp.description}
                  </p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100">
                    {exp.tags.map((tag, i) => (
                      <span key={i} className="text-[10px] tracking-wider uppercase text-zinc-500">
                        {tag}{i < exp.tags.length - 1 ? ' •' : ''}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Top accent line */}
                <div className="absolute top-0 left-0 w-full h-px bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExperiencesSection;
